import React, { useRef, useState, useEffect, useContext, useCallback } from 'react';
import { useFrame } from '@react-three/fiber';
import { Tower as TowerType, useGameStore } from '../store/gameStore';
import { Text, Billboard, useGLTF } from '@react-three/drei';
import { Group, Vector3 } from 'three';
import * as THREE from 'three';
import ReactDOM from 'react-dom';
import { useDrawingCanvas } from '../components/DrawingCanvasManager';
import { useJokeInput } from '../components/JokeInputManager';
import { useResponsive } from '../hooks/useResponsive';
import PortalTower from './PortalTower';
// Import global type declarations
import '../types/global';
// Import the tower helpers utility
import { setJokeSubmitHandler, clearJokeSubmitHandler, setDrawingSubmitHandler, clearDrawingSubmitHandler } from '../utils/towerHelpers';
// Import persistence utils
import { saveJokesToStorage, getJokesFromStorage } from '../utils/persistenceUtils';

// Define the path to the math tower model in the public directory
const MATH_TOWER_MODEL_PATH = '/models/math-tower-model.glb';

// Helper function to format position for display
const formatPosition = (pos: Vector3): string => {
  return `(${pos.x.toFixed(1)}, ${pos.y.toFixed(1)}, ${pos.z.toFixed(1)})`;
};

// Interface for math questions
interface MathQuestion {
  question: string;
  answer: number;
  options: number[];
}

// Interface for science questions
interface ScienceQuestion {
  question: string;
  answer: string;
  options: string[];
  category: string;
}

interface ShootingEffect {
  id: string;
  targetPosition: Vector3;
  startTime: number;
  duration: number;
  spellType: string;
  targetId?: string;
}

interface TowerProps {
  tower: TowerType;
}

// Load the math tower model
const MathTowerModel = () => {
  console.log('Loading math tower model...');
  const { scene } = useGLTF(MATH_TOWER_MODEL_PATH);
  console.log('Math tower model loaded:', scene);
  
  return (
    <group>
      {/* Apply transformations to fix orientation */}
      <group scale={[3.0, 3.0, 3.0]} position={[0, 0, 0]} rotation={[0, 0, 0]}>
        {/* Apply additional rotation to fix sideways orientation */}
        <group rotation={[-Math.PI/2, 0, 0]}>
          <primitive 
            object={scene.clone()} 
            position={[0, 0, 0]}
          />
        </group>
      </group>
      
      {/* Add a base for the math tower */}
      <mesh position={[0, 0.1, 0]} castShadow receiveShadow>
        <cylinderGeometry args={[0.8, 1, 0.2, 16]} />
        <meshStandardMaterial color="#4444aa" />
      </mesh>
    </group>
  );
};

const Tower: React.FC<TowerProps> = ({ tower }) => {
  // If it's a portal tower, render the PortalTower component
  if (tower.type === 'portal') {
    return <PortalTower tower={tower} />;
  }

  const { selectedTower, selectTower, upgradeTower, sellTower, enemies } = useGameStore();
  const isSelected = selectedTower?.id === tower.id;
  const [hovered, setHovered] = useState(false);
  // Add specific hover states for buttons
  const [upgradeButtonHovered, setUpgradeButtonHovered] = useState(false);
  const [sellButtonHovered, setSellButtonHovered] = useState(false);
  const [jokeButtonHovered, setJokeButtonHovered] = useState(false);
  // Add animation states for buttons
  const [upgradeButtonClicked, setUpgradeButtonClicked] = useState(false);
  const [upgradeButtonScale, setUpgradeButtonScale] = useState(1);
  const [upgradeButtonRotation, setUpgradeButtonRotation] = useState(0);
  const [upgradeFlashOpacity, setUpgradeFlashOpacity] = useState(0);
  const upgradeButtonRef = useRef<THREE.Mesh>(null);
  
  // Add animation states for sell button
  const [sellButtonClicked, setSellButtonClicked] = useState(false);
  const [sellButtonScale, setSellButtonScale] = useState(1);
  const [sellButtonRotation, setSellButtonRotation] = useState(0);
  const [sellFlashOpacity, setSellFlashOpacity] = useState(0);
  const sellButtonRef = useRef<THREE.Mesh>(null);
  // Add debounce flags to prevent rapid clicking
  const [sellButtonDisabled, setSellButtonDisabled] = useState(false);
  const [jokeButtonDisabled, setJokeButtonDisabled] = useState(false);
  
  // Add animation states for joke button
  const [jokeButtonClicked, setJokeButtonClicked] = useState(false);
  const [jokeButtonScale, setJokeButtonScale] = useState(1);
  const [jokeButtonRotation, setJokeButtonRotation] = useState(0);
  const [jokeFlashOpacity, setJokeFlashOpacity] = useState(0);
  const jokeButtonRef = useRef<THREE.Mesh>(null);
  
  const rotationRef = useRef(0);
  const topRef = useRef<Group>(null);
  const lastShotTime = useRef(0);
  const [shootingEffects, setShootingEffects] = useState<ShootingEffect[]>([]);
  
  // Get responsive sizes based on device
  const { isMobile, scaleFactor } = useResponsive();
  
  // Math question state
  const [showMathQuestion, setShowMathQuestion] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState<MathQuestion | null>(null);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [answerResult, setAnswerResult] = useState<'correct' | 'incorrect' | null>(null);
  
  // Science question state
  const [showScienceQuestion, setShowScienceQuestion] = useState(false);
  const [currentScienceQuestion, setCurrentScienceQuestion] = useState<ScienceQuestion | null>(null);
  const [selectedScienceAnswer, setSelectedScienceAnswer] = useState<string | null>(null);
  const [scienceAnswerResult, setScienceAnswerResult] = useState<'correct' | 'incorrect' | null>(null);
  
  // Property boost effect state
  const [showPropertyBoost, setShowPropertyBoost] = useState(false);
  const [boostedProperty, setBoostedProperty] = useState<string>('');
  const propertyBoostRef = useRef<number | null>(null);
  const propertyBoostTimeRef = useRef(0);
  
  // Speed boost state
  const [showSpeedBoost, setShowSpeedBoost] = useState(false);
  const speedBoostRef = useRef<number | null>(null);
  const speedBoostTimeRef = useRef(0);
  
  // Art joke state
  const [showJokeEffect, setShowJokeEffect] = useState(false);
  const [currentJoke, setCurrentJoke] = useState<string | null>(null);
  const jokeEffectRef = useRef<number | null>(null);
  const jokeEffectTimeRef = useRef(0);
  const [isShowingJoke, setIsShowingJoke] = useState(false);
  const [jokeDisplayTime, setJokeDisplayTime] = useState(0);
  
  // Art effect state
  const [showArtEffect, setShowArtEffect] = useState(false);
  const artEffectRef = useRef<number | null>(null);
  const artEffectTimeRef = useRef(0);
  
  // Get the drawing canvas context
  const { showDrawingCanvas } = useDrawingCanvas();
  
  // Get the joke input context
  const { showJokeInput } = useJokeInput();
  
  // Animate the speed boost effect
  useFrame((state) => {
    if (showSpeedBoost) {
      speedBoostTimeRef.current = state.clock.getElapsedTime();
    }
  });
  
  // Add animation values for the joke effect
  const [jokeEffectScale, setJokeEffectScale] = useState(1);
  const [jokeEffectOpacity, setJokeEffectOpacity] = useState(0);
  const [jokeTextBounce, setJokeTextBounce] = useState(0);
  
  // Animate the joke effect with separate hook to ensure it runs independently
  useFrame((state, delta) => {
    // Only run for drama towers with the joke effect active
    if (tower.type === 'drama' && showJokeEffect) {
      // Increment the time reference
      jokeEffectTimeRef.current += delta;
      
      // Animation values
      setJokeEffectScale(1 + Math.sin(state.clock.elapsedTime * 2) * 0.1);
      setJokeEffectOpacity(0.6 + Math.sin(state.clock.elapsedTime * 3) * 0.2);
      setJokeTextBounce(Math.sin(state.clock.elapsedTime * 4) * 0.2);
      
      if (jokeEffectTimeRef.current < 0.1) {
        console.log("Joke effect animation running for drama tower", tower.id);
      }
    }
  });
  
  // Add effect to initialize jokeEffectTimeRef when showJokeEffect is turned on
  useEffect(() => {
    if (showJokeEffect) {
      // Initialize the effect time reference
      jokeEffectTimeRef.current = 0;
      console.log(`Joke effect activated for tower ${tower.id} (type: ${tower.type})`);
    }
  }, [showJokeEffect, tower.id, tower.type]);
  
  // Handle joke display and timing
  useFrame((state) => {
    // Set joke display time when a joke is first shown
    if (isShowingJoke && jokeDisplayTime === 0) {
      setJokeDisplayTime(state.clock.getElapsedTime());
      console.log("Started showing joke:", currentJoke);
    }
    
    // Auto-hide joke after 5 seconds
    if (isShowingJoke && jokeDisplayTime > 0 && state.clock.getElapsedTime() - jokeDisplayTime > 5) {
      console.log("Auto-hiding joke bubble after 5 seconds");
      setIsShowingJoke(false);
      setCurrentJoke(null);
      setJokeDisplayTime(0);
    }
  });
  
  // Generate a random math question based on tower level
  const generateMathQuestion = () => {
    // Adjust difficulty based on tower level
    const level = tower.level;
    let num1, num2, operation, question, answer, options;
    
    if (level === 1) {
      // Addition and subtraction for level 1
      num1 = Math.floor(Math.random() * 20) + 1;
      num2 = Math.floor(Math.random() * 20) + 1;
      operation = Math.random() > 0.5 ? '+' : '-';
      
      if (operation === '-' && num2 > num1) {
        // Swap to avoid negative answers for beginners
        [num1, num2] = [num2, num1];
      }
      
      question = `${num1} ${operation} ${num2} = ?`;
      answer = operation === '+' ? num1 + num2 : num1 - num2;
    } else if (level === 2) {
      // Multiplication and division for level 2
      num1 = Math.floor(Math.random() * 12) + 1;
      num2 = Math.floor(Math.random() * 12) + 1;
      operation = Math.random() > 0.5 ? '×' : '÷';
      
      if (operation === '÷') {
        // Ensure clean division
        const product = num1 * num2;
        num1 = product;
        question = `${num1} ${operation} ${num2} = ?`;
        answer = num1 / num2;
      } else {
        question = `${num1} ${operation} ${num2} = ?`;
        answer = num1 * num2;
      }
    } else {
      // More complex operations for level 3+
      num1 = Math.floor(Math.random() * 30) + 10;
      num2 = Math.floor(Math.random() * 20) + 5;
      const operations = ['+', '-', '×'];
      operation = operations[Math.floor(Math.random() * operations.length)];
      
      if (operation === '+') {
        question = `${num1} + ${num2} = ?`;
        answer = num1 + num2;
      } else if (operation === '-') {
        question = `${num1} - ${num2} = ?`;
        answer = num1 - num2;
      } else {
        question = `${num1} × ${num2} = ?`;
        answer = num1 * num2;
      }
    }
    
    // Generate options (including the correct answer)
    options = [answer];
    
    // Add 3 wrong answers
    while (options.length < 4) {
      // Generate a wrong answer within a reasonable range of the correct answer
      let wrongAnswer;
      if (operation === '+' || operation === '-') {
        wrongAnswer = answer + (Math.floor(Math.random() * 10) - 5);
      } else if (operation === '×') {
        wrongAnswer = answer + (Math.floor(Math.random() * (answer / 2)) - (answer / 4));
      } else { // division
        wrongAnswer = answer + (Math.floor(Math.random() * 5) - 2);
      }
      
      // Ensure the wrong answer is positive and not a duplicate
      if (wrongAnswer > 0 && !options.includes(wrongAnswer)) {
        options.push(wrongAnswer);
      }
    }
    
    // Shuffle options using Fisher-Yates algorithm for better randomization
    for (let i = options.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [options[i], options[j]] = [options[j], options[i]];
    }
    
    return { question, answer, options };
  };
  
  // Generate a random drawing prompt for art tower
  const generateDrawingPrompt = () => {
    const prompts = [
      "Draw a beautiful sunset",
      "Draw a colorful flower",
      "Draw a peaceful landscape",
      "Draw a self-portrait",
      "Draw a still life",
      "Draw an abstract pattern",
      "Draw a cityscape",
      "Draw a fantasy creature",
      "Draw a geometric design",
      "Draw a nature scene"
    ];
    
    return prompts[Math.floor(Math.random() * prompts.length)];
  };
  
  // Handle answer selection
  const handleAnswerSelect = (selectedValue: number) => {
    setSelectedAnswer(selectedValue);
    
    if (currentQuestion && selectedValue === currentQuestion.answer) {
      setAnswerResult('correct');
      
      // Wait a moment to show the result, then upgrade the tower
      setTimeout(() => {
        // For math towers, increase shooting speed by 0.1 in addition to regular upgrade
        if (tower.type === 'math') {
          // Custom upgrade for math tower - increase fire rate by additional 0.1
          const customUpgrade = {
            ...tower,
            fireRate: tower.fireRate + 0.1, // Increase fire rate by 0.1
            // Also apply standard upgrades
            level: tower.level,
            range: tower.range * 1.2,
            damage: tower.damage * 1.3
          };
          upgradeTower(tower.id, customUpgrade);
          
          // Show speed boost effect
          setShowSpeedBoost(true);
          
          // Clear any existing timeout
          if (speedBoostRef.current) {
            clearTimeout(speedBoostRef.current);
          }
          
          // Hide the effect after 3 seconds
          speedBoostRef.current = window.setTimeout(() => {
            setShowSpeedBoost(false);
            speedBoostRef.current = null;
          }, 3000);
        } else {
          upgradeTower(tower.id);
        }
        resetMathQuestion();
      }, 1000);
    } else {
      setAnswerResult('incorrect');
      
      // Reset after showing the incorrect result
      setTimeout(() => {
        setSelectedAnswer(null);
        setAnswerResult(null);
      }, 1000);
    }
  };
  
  // Handle drawing submission for art towers
  const handleDrawingSubmit = useCallback(() => {
    // Apply a special upgrade for art towers
    const customUpgrade = {
      ...tower,
      level: tower.level + 1,
      // Add a special bonus for art towers - increase range by 40% total
      range: tower.range * 1.4, 
      damage: tower.damage * 1.3,
      fireRate: tower.fireRate * 1.1
    };
    
    upgradeTower(tower.id, customUpgrade);
    
    // Show art effect
    setShowArtEffect(true);
    
    // Clear any existing timeout
    if (artEffectRef.current) {
      clearTimeout(artEffectRef.current);
    }
    
    // Hide the effect after 3 seconds
    artEffectRef.current = window.setTimeout(() => {
      setShowArtEffect(false);
      artEffectRef.current = null;
    }, 3000);
  }, [tower, upgradeTower, setShowArtEffect]);
  
  // Reset the math question state
  const resetMathQuestion = () => {
    setShowMathQuestion(false);
    setCurrentQuestion(null);
    setSelectedAnswer(null);
    setAnswerResult(null);
  };
  
  // Rotate the tower top
  useFrame((state, delta) => {
    if (topRef.current) {
      rotationRef.current += delta * 0.5;
      
      // Find enemies in range
      const enemiesInRange = enemies.filter(
        enemy => enemy.position.distanceTo(tower.position) <= tower.range
      );
      
      // If there are enemies in range, rotate towards the closest one
      if (enemiesInRange.length > 0) {
        // Sort by path index (closest to end of path first)
        const target = enemiesInRange.sort((a, b) => b.pathIndex - a.pathIndex)[0];
        
        // Calculate direction to target
        const direction = new Vector3().subVectors(target.position, tower.position).normalize();
        
        // Calculate angle to target
        const angle = Math.atan2(direction.x, direction.z);
        
        // Smoothly rotate towards target
        topRef.current.rotation.y = angle;
        
        // Check if it's time to shoot
        if (state.clock.getElapsedTime() - lastShotTime.current > 1 / tower.fireRate) {
          lastShotTime.current = state.clock.getElapsedTime();
          
          // Determine spell type based on tower type
          let spellType = '';
          switch (tower.type) {
            case 'science':
              spellType = ['stupefy', 'expelliarmus', 'impedimenta'][Math.floor(Math.random() * 3)];
              break;
            case 'math':
              spellType = ['wingardium', 'accio', 'reducto'][Math.floor(Math.random() * 3)];
              break;
            case 'art':
              spellType = ['incendio', 'aguamenti', 'colovaria'][Math.floor(Math.random() * 3)];
              break;
            case 'drama':
              spellType = ['glacius', 'immobulus', 'petrificus'][Math.floor(Math.random() * 3)];
              break;
            default:
              spellType = 'stupefy';
          }
          
          // Add shooting effect
          const newEffect: ShootingEffect = {
            id: `shot-${Date.now()}-${Math.random()}`,
            targetPosition: target.position.clone(),
            startTime: state.clock.getElapsedTime(),
            duration: 0.6, // Longer duration for more dramatic effect
            spellType,
            targetId: target.id // Store the target ID immediately
          };
          
          setShootingEffects(prev => [...prev, newEffect]);
        }
      } else {
        // If no enemies in range, continue rotating
        topRef.current.rotation.y = rotationRef.current;
      }
    }
    
    // Update shooting effects
    setShootingEffects(prev => 
      prev.filter(effect => state.clock.getElapsedTime() - effect.startTime < effect.duration)
    );
  });
  
  // Handle tower click
  const handleClick = (e: any) => {
    e.stopPropagation();
    selectTower(tower.id);
  };
  
  // Tower colors based on type
  const getTowerColors = () => {
    switch (tower.type) {
      case 'science':
        return {
          base: '#4a4ae0',
          top: '#6a6af0',
          accent: '#2a2ac0',
          beam: '#ff0000', // Red for Gryffindor
          spell: '#ff3333'
        };
      case 'math':
        return {
          base: '#e04a4a',
          top: '#f06a6a',
          accent: '#c02a2a',
          beam: '#0066ff', // Blue for Ravenclaw
          spell: '#3399ff'
        };
      case 'art':
        return {
          base: '#4ae04a',
          top: '#6af06a',
          accent: '#2ac02a',
          beam: '#ffcc00', // Yellow for Hufflepuff
          spell: '#ffdd44'
        };
      case 'drama':
        return {
          base: '#9932cc', // Purple for Drama
          top: '#b760e0',
          accent: '#7b2c9d',
          beam: '#00ffff', // Cyan for freezing effect
          spell: '#80ffff'
        };
      default:
        return {
          base: '#4a4ae0',
          top: '#6a6af0',
          accent: '#2a2ac0',
          beam: '#00ff00',
          spell: '#33ff33'
        };
    }
  };
  
  const colors = getTowerColors();
  
  // Render different tower types (now as wizards with wands)
  const renderTowerTop = () => {
    switch (tower.type) {
      case 'science':
        return (
          <>
            {/* Science tower - Gryffindor wizard */}
            {/* Wizard body */}
            <mesh position={[0, 1.2, 0]}>
              <cylinderGeometry args={[0.4, 0.6, 1.2, 16]} />
              <meshStandardMaterial color="#880000" /> {/* Gryffindor robe */}
            </mesh>
            
            {/* Wizard head */}
            <mesh position={[0, 2.0, 0]}>
              <sphereGeometry args={[0.3, 16, 16]} />
              <meshStandardMaterial color="#ffdbac" /> {/* Skin tone */}
            </mesh>
            
            {/* Wizard hat */}
            <mesh position={[0, 2.4, 0]}>
              <coneGeometry args={[0.3, 0.6, 16]} />
              <meshStandardMaterial color="#222222" />
            </mesh>
            
            {/* Wand */}
            <mesh position={[0.3, 1.5, 0.4]} rotation={[0, 0, Math.PI / 4]}>
              <cylinderGeometry args={[0.03, 0.03, 0.8, 8]} />
              <meshStandardMaterial color="#663300" />
            </mesh>
            
            {/* Wand tip */}
            <mesh position={[0.6, 1.8, 0.7]}>
              <sphereGeometry args={[0.05, 8, 8]} />
              <meshStandardMaterial color="#ffffff" emissive="#ff0000" emissiveIntensity={0.8} />
            </mesh>
            
            {/* Debug wand tip position - REMOVED */}
            {/* 
            <mesh position={[0.7, 0, 0]}>
              <sphereGeometry args={[0.08, 8, 8]} />
              <meshStandardMaterial color="#00ff00" emissive="#00ff00" emissiveIntensity={1.0} />
            </mesh>
            */}
          </>
        );
      case 'math':
        return (
          <>
            {/* Math tower - Using the 3D model */}
            <MathTowerModel />
            
            {/* Wand tip for shooting effects */}
            <mesh position={[0.6, 1.8, 0.7]} visible={false}>
              <sphereGeometry args={[0.05, 8, 8]} />
              <meshStandardMaterial color="#ffffff" emissive="#ffcc00" emissiveIntensity={0.8} />
            </mesh>
            
            {/* Debug wand tip position - REMOVED */}
            {/*
            <mesh position={[0.7, 0, 0]}>
              <sphereGeometry args={[0.08, 8, 8]} />
              <meshStandardMaterial color="#00ff00" emissive="#00ff00" emissiveIntensity={1.0} />
            </mesh>
            */}
          </>
        );
      case 'art':
        return (
          <>
            {/* Art tower - Hufflepuff wizard */}
            {/* Wizard body */}
            <mesh position={[0, 1.2, 0]}>
              <cylinderGeometry args={[0.4, 0.6, 1.2, 16]} />
              <meshStandardMaterial color="#dddd00" /> {/* Hufflepuff robe */}
            </mesh>
            
            {/* Wizard head */}
            <mesh position={[0, 2.0, 0]}>
              <sphereGeometry args={[0.3, 16, 16]} />
              <meshStandardMaterial color="#ffdbac" /> {/* Skin tone */}
            </mesh>
            
            {/* Wizard hat */}
            <mesh position={[0, 2.4, 0]}>
              <coneGeometry args={[0.3, 0.6, 16]} />
              <meshStandardMaterial color="#666622" />
            </mesh>
            
            {/* Wand */}
            <mesh position={[0.3, 1.5, 0.4]} rotation={[0, 0, Math.PI / 4]}>
              <cylinderGeometry args={[0.03, 0.03, 0.6, 8]} />
              <meshStandardMaterial color="#663300" />
            </mesh>
            
            {/* Wand tip */}
            <mesh position={[0.6, 1.8, 0.7]}>
              <sphereGeometry args={[0.05, 8, 8]} />
              <meshStandardMaterial color="#ffffff" emissive="#ff0000" emissiveIntensity={0.8} />
            </mesh>
            
            {/* Debug wand tip position - REMOVED */}
            {/*
            <mesh position={[0.7, 0, 0]}>
              <sphereGeometry args={[0.08, 8, 8]} />
              <meshStandardMaterial color="#00ff00" emissive="#00ff00" emissiveIntensity={1.0} />
            </mesh>
            */}
            
            {/* Paintbrush/wand */}
            <mesh position={[-0.3, 1.5, 0.2]} rotation={[0, 0, -Math.PI / 4]}>
              <cylinderGeometry args={[0.03, 0.03, 0.6, 8]} />
              <meshStandardMaterial color="#663300" />
            </mesh>
            <mesh position={[-0.6, 1.3, 0.2]} rotation={[0, 0, -Math.PI / 4]}>
              <cylinderGeometry args={[0.06, 0.01, 0.2, 8]} />
              <meshStandardMaterial color="#ff00ff" />
            </mesh>
            
            {/* Wand tip */}
            <mesh position={[0.6, 1.8, 0.7]}>
              <sphereGeometry args={[0.05, 8, 8]} />
              <meshStandardMaterial color="#ffffff" emissive="#00ffff" emissiveIntensity={0.8} />
            </mesh>
            
            {/* Debug wand tip position - REMOVED */}
            {/*
            <mesh position={[0.7, 0, 0]}>
              <sphereGeometry args={[0.08, 8, 8]} />
              <meshStandardMaterial color="#00ff00" emissive="#00ff00" emissiveIntensity={1.0} />
            </mesh>
            */}
            
            {/* Floating mask (drama symbol) */}
            <mesh position={[-0.4, 1.5, 0.2]} rotation={[0, Math.PI / 6, 0]}>
              <sphereGeometry args={[0.15, 8, 8, 0, Math.PI]} />
              <meshStandardMaterial color="#d8bfd8" side={2} />
            </mesh>
          </>
        );
      case 'drama':
        return (
          <>
            {/* Drama tower - Slytherin-inspired wizard */}
            {/* Wizard body */}
            <mesh position={[0, 1.2, 0]}>
              <cylinderGeometry args={[0.4, 0.6, 1.2, 16]} />
              <meshStandardMaterial color="#9932cc" /> {/* Purple robe */}
            </mesh>
            
            {/* Wizard head */}
            <mesh position={[0, 2.0, 0]}>
              <sphereGeometry args={[0.3, 16, 16]} />
              <meshStandardMaterial color="#ffdbac" /> {/* Skin tone */}
            </mesh>
            
            {/* Wizard hat */}
            <mesh position={[0, 2.4, 0]}>
              <coneGeometry args={[0.3, 0.6, 16]} />
              <meshStandardMaterial color="#4b0082" /> {/* Indigo hat */}
            </mesh>
            
            {/* Wand */}
            <mesh position={[0.3, 1.5, 0.4]} rotation={[0, 0, Math.PI / 4]}>
              <cylinderGeometry args={[0.03, 0.03, 0.8, 8]} />
              <meshStandardMaterial color="#663300" />
            </mesh>
            
            {/* Wand tip */}
            <mesh position={[0.6, 1.8, 0.7]}>
              <sphereGeometry args={[0.05, 8, 8]} />
              <meshStandardMaterial color="#ffffff" emissive="#00ffff" emissiveIntensity={0.8} />
            </mesh>
            
            {/* Debug wand tip position - REMOVED */}
            {/*
            <mesh position={[0.7, 0, 0]}>
              <sphereGeometry args={[0.08, 8, 8]} />
              <meshStandardMaterial color="#00ff00" emissive="#00ff00" emissiveIntensity={1.0} />
            </mesh>
            */}
            
            {/* Floating mask (drama symbol) */}
            <mesh position={[-0.4, 1.5, 0.2]} rotation={[0, Math.PI / 6, 0]}>
              <sphereGeometry args={[0.15, 8, 8, 0, Math.PI]} />
              <meshStandardMaterial color="#d8bfd8" side={2} />
            </mesh>
          </>
        );
      default:
        return null;
    }
  };

  // Render shooting effects (Harry Potter style spells)
  const renderShootingEffects = () => {
    return shootingEffects.map(effect => {
      // Find the current position of the target enemy (if it still exists)
      const targetEnemy = enemies.find(enemy => 
        enemy.id === effect.targetId
      );
      
      // Use the tracked enemy's position if available, otherwise use the original target position
      const currentTargetPosition = targetEnemy ? targetEnemy.position.clone() : effect.targetPosition;
      
      // Get the current rotation angle of the tower top
      const angle = topRef.current ? topRef.current.rotation.y : 0;
      
      // Calculate the wand tip position in world space
      // First, calculate the local offset from the tower center
      const localOffset = new Vector3(
        Math.sin(angle) * 0.6 + Math.sin(angle + Math.PI/4) * 0.3,
        1.8,
        Math.cos(angle) * 0.6 + Math.cos(angle + Math.PI/4) * 0.3
      );
      
      // Convert to world space by adding tower position
      const wandTipPosition = new Vector3(
        tower.position.x + localOffset.x,
        tower.position.y + localOffset.y,
        tower.position.z + localOffset.z
      );
      
      // Calculate progress of the spell (0 to 1)
      const progress = Math.min(1, (Date.now() / 1000 - effect.startTime) / (effect.duration * 0.8));
      
      // Calculate current position of the spell - direct line from wand tip to target
      const currentPosition = new Vector3().lerpVectors(
        wandTipPosition,
        currentTargetPosition,
        progress
      );
      
      // Different spell effects based on tower type and spell
      switch (tower.type) {
        case 'science': // Gryffindor - combat spells
          return (
            <group key={effect.id} position={[0, 0, 0]}>
              {/* Spell trail */}
              {Array.from({ length: 10 }).map((_, i) => {
                const t = i / 9; // Normalized position along path
                const trailPos = new Vector3().lerpVectors(
                  wandTipPosition,
                  currentPosition,
                  t
                );
                
                // Add some waviness to the trail
                const wave = Math.sin(t * Math.PI * 4 + Date.now() / 100) * 0.1;
                trailPos.y += wave;
                
                return (
                  <mesh key={`trail-${i}`} position={trailPos}>
                    <sphereGeometry args={[0.1 * (1 - t * 0.7), 8, 8]} />
                    <meshStandardMaterial color={colors.beam} transparent opacity={(1 - t) * 0.8} />
                  </mesh>
                );
              })}
              
              {/* Spell head */}
              <mesh position={currentPosition}>
                <sphereGeometry args={[0.2, 16, 16]} />
                <meshStandardMaterial color={colors.beam} transparent opacity={0.9} />
              </mesh>
              
              {/* Spell name */}
              <Billboard
                position={[currentPosition.x, currentPosition.y + 0.4, currentPosition.z]}
                follow={true}
                lockX={false}
                lockY={false}
                lockZ={false}
              >
                <Text
                  position={[0, 0, 0]}
                  fontSize={0.3}
                  color={colors.spell}
                  anchorX="center"
                  anchorY="middle"
                  fontWeight="bold"
                >
                  {effect.spellType}
                </Text>
              </Billboard>
              
              {/* Impact effect when spell reaches target */}
              {progress >= 0.95 && (
                <group position={currentTargetPosition}>
                  <mesh>
                    <sphereGeometry args={[0.4, 16, 16]} />
                    <meshStandardMaterial color={colors.beam} transparent opacity={0.7} />
                  </mesh>
                  <pointLight color={colors.beam} intensity={2} distance={3} decay={2} />
                </group>
              )}
            </group>
          );
        case 'math': // Ravenclaw - clever spells
          return (
            <group key={effect.id} position={[0, 0, 0]}>
              {/* Math symbols trail */}
              {Array.from({ length: 10 }).map((_, i) => {
                const t = i / 9; // Normalized position along path
                const symbolPos = new Vector3().lerpVectors(
                  wandTipPosition,
                  currentPosition,
                  t
                );
                
                // Add some waviness to the trail
                const wave = Math.sin(t * Math.PI * 4 + Date.now() / 100) * 0.1;
                symbolPos.y += wave;
                
                // Math symbols: π, ∑, ∫, √, ∞, etc.
                const symbols = ["π", "∑", "∫", "√", "∞", "θ", "Δ", "λ", "Ω", "φ"];
                const symbol = symbols[Math.floor(Math.random() * symbols.length)];
                
                return (
                  <Billboard
                    key={`symbol-${i}`}
                    position={symbolPos}
                    follow={true}
                    lockX={false}
                    lockY={false}
                    lockZ={false}
                  >
                    <Text
                      position={[0, 0, 0]}
                      fontSize={0.2}
                      color="#4b9cd3"
                      anchorX="center"
                      anchorY="middle"
                      fontWeight="bold"
                    >
                      {symbol}
                    </Text>
                  </Billboard>
                );
              })}
              
              {/* Spell head */}
              <mesh position={currentPosition}>
                <sphereGeometry args={[0.2, 16, 16]} />
                <meshStandardMaterial color="#4b9cd3" transparent opacity={0.9} />
              </mesh>
              
              {/* Spell name */}
              <Billboard
                position={[currentPosition.x, currentPosition.y + 0.4, currentPosition.z]}
                follow={true}
                lockX={false}
                lockY={false}
                lockZ={false}
              >
                <Text
                  position={[0, 0, 0]}
                  fontSize={0.3}
                  color="#4b9cd3"
                  anchorX="center"
                  anchorY="middle"
                  fontWeight="bold"
                >
                  {effect.spellType}
                </Text>
              </Billboard>
              
              {/* Impact effect */}
              {progress > 0.9 && (
                <group position={currentTargetPosition}>
                  <mesh>
                    <sphereGeometry args={[0.4, 16, 16]} />
                    <meshStandardMaterial color="#4b9cd3" transparent opacity={0.7} />
                  </mesh>
                  <pointLight color="#4b9cd3" intensity={2} distance={3} decay={2} />
                </group>
              )}
            </group>
          );
        case 'art': // Hufflepuff - colorful spells
          return (
            <group key={effect.id} position={[0, 0, 0]}>
              {/* Colorful paint splatter trail */}
              {Array.from({ length: 12 }).map((_, i) => {
                const t = i / 11; // Normalized position along path
                const splatterPos = new Vector3().lerpVectors(
                  wandTipPosition,
                  currentPosition,
                  t
                );
                
                // Add some random offset
                splatterPos.x += (Math.random() - 0.5) * 0.3;
                splatterPos.y += (Math.random() - 0.5) * 0.3;
                splatterPos.z += (Math.random() - 0.5) * 0.3;
                
                // Random color for each splatter
                const colors = ["#ff0000", "#00ff00", "#0000ff", "#ffff00", "#ff00ff", "#00ffff"];
                const color = colors[Math.floor(Math.random() * colors.length)];
                
                return (
                  <mesh key={`splatter-${i}`} position={splatterPos}>
                    <sphereGeometry args={[0.1 + Math.random() * 0.1, 8, 8]} />
                    <meshStandardMaterial color={color} transparent opacity={(1 - t * 0.5) * 0.8} />
                  </mesh>
                );
              })}
              
              {/* Spell head */}
              <mesh position={currentPosition}>
                <sphereGeometry args={[0.25, 16, 16]} />
                <meshStandardMaterial color="#ffffff" transparent opacity={0.9} />
              </mesh>
              
              {/* Spell name */}
              <Billboard
                position={[currentPosition.x, currentPosition.y + 0.4, currentPosition.z]}
                follow={true}
                lockX={false}
                lockY={false}
                lockZ={false}
              >
                <Text
                  position={[0, 0, 0]}
                  fontSize={0.3}
                  color="#ffffff"
                  anchorX="center"
                  anchorY="middle"
                  fontWeight="bold"
                >
                  {effect.spellType}
                </Text>
              </Billboard>
              
              {/* Impact effect */}
              {progress > 0.9 && (
                <group position={currentTargetPosition}>
                  <mesh>
                    <sphereGeometry args={[0.4, 16, 16]} />
                    <meshStandardMaterial color="#ffffff" transparent opacity={0.7} />
                  </mesh>
                  <pointLight color="#ffffff" intensity={2} distance={3} decay={2} />
                </group>
              )}
            </group>
          );
        case 'drama': // Slytherin - freezing spells
          return (
            <group key={effect.id} position={[0, 0, 0]}>
              {/* Freezing spell trail */}
              {Array.from({ length: 10 }).map((_, i) => {
                const t = i / 9; // Normalized position along path
                const icePos = new Vector3().lerpVectors(
                  wandTipPosition,
                  currentPosition,
                  t
                );
                
                // Add some waviness to the trail
                const wave = Math.sin(t * Math.PI * 4 + Date.now() / 100) * 0.1;
                icePos.y += wave;
                
                return (
                  <mesh key={`ice-${i}`} position={icePos}>
                    <icosahedronGeometry args={[0.1 * (1 - t * 0.7), 0]} />
                    <meshStandardMaterial color={colors.beam} transparent opacity={(1 - t) * 0.8} />
                  </mesh>
                );
              })}
              
              {/* Spell head */}
              <mesh position={currentPosition}>
                <icosahedronGeometry args={[0.2, 0]} />
                <meshStandardMaterial color={colors.beam} transparent opacity={0.9} />
              </mesh>
              
              {/* Spell name */}
              <Billboard
                position={[currentPosition.x, currentPosition.y + 0.4, currentPosition.z]}
                follow={true}
                lockX={false}
                lockY={false}
                lockZ={false}
              >
                <Text
                  position={[0, 0, 0]}
                  fontSize={0.3}
                  color={colors.beam}
                  anchorX="center"
                  anchorY="middle"
                  fontWeight="bold"
                >
                  {effect.spellType}
                </Text>
              </Billboard>
              
              {/* Impact effect */}
              {progress > 0.9 && (
                <group position={currentTargetPosition}>
                  <mesh>
                    <sphereGeometry args={[0.4, 16, 16]} />
                    <meshStandardMaterial color={colors.beam} transparent opacity={0.7} />
                  </mesh>
                  <pointLight color={colors.beam} intensity={2} distance={3} decay={2} />
                </group>
              )}
            </group>
          );
        default:
          return null;
      }
    });
  };
  
  // Render enemy position labels
  const renderEnemyLabels = () => {
    return enemies
      .filter(enemy => enemy.position.distanceTo(tower.position) <= tower.range)
      .map(enemy => (
        <Billboard
          key={`enemy-label-${enemy.id}`}
          position={[enemy.position.x, enemy.position.y + 2.5, enemy.position.z]}
          follow={true}
          lockX={false}
          lockY={false}
          lockZ={false}
        >
          {/* Background for better visibility */}
          <mesh position={[0, 0, -0.01]}>
            <planeGeometry args={[3.0, 0.8]} />
            <meshBasicMaterial color="black" transparent opacity={0.8} />
          </mesh>
          
          {/* Text label */}
          <Text
            position={[0, 0, 0]}
            fontSize={0.4}
            color="#ff3333"
            anchorX="center"
            anchorY="middle"
            fontWeight="bold"
          >
            {`Alien: ${formatPosition(enemy.position)}`}
          </Text>
        </Billboard>
      ));
  };
  
  // Visualization of calculated wand tip position
  const renderWandTipVisualization = () => {
    if (enemies.length === 0) return null;
    
    // Get the current rotation angle of the tower top
    const angle = topRef.current ? topRef.current.rotation.y : 0;
    
    // Calculate the local offset from the tower center
    const localOffset = new Vector3(
      Math.sin(angle) * 0.6 + Math.sin(angle + Math.PI/4) * 0.3,
      1.8,
      Math.cos(angle) * 0.6 + Math.cos(angle + Math.PI/4) * 0.3
    );
    
    // Calculate the world position
    const worldPosition = new Vector3(
      tower.position.x + localOffset.x,
      tower.position.y + localOffset.y,
      tower.position.z + localOffset.z
    );
    
    return (
      <>
        {/* Actual wand tip position used in calculations (local to tower) */}
        <mesh position={localOffset}>
          <sphereGeometry args={[0.1, 16, 16]} />
          <meshStandardMaterial color="#ff00ff" emissive="#ff00ff" emissiveIntensity={1.0} />
        </mesh>
        
        {/* Label for local wand tip position */}
        <Billboard
          position={[localOffset.x, localOffset.y + 0.4, localOffset.z]}
          follow={true}
          lockX={false}
          lockY={false}
          lockZ={false}
        >
          <mesh position={[0, 0, -0.01]}>
            <planeGeometry args={[3.0, 0.8]} />
            <meshBasicMaterial color="black" transparent opacity={0.8} />
          </mesh>
          <Text
            position={[0, 0, 0]}
            fontSize={0.25}
            color="#ff00ff"
            anchorX="center"
            anchorY="middle"
            fontWeight="bold"
          >
            {`Wand Tip (Local): ${formatPosition(localOffset)}`}
          </Text>
        </Billboard>
      </>
    );
  };
  
  // Render math question UI with responsive sizing
  const renderMathQuestion = () => {
    if (!showMathQuestion || !currentQuestion) return null;
    
    // Adjust sizes for mobile
    const boxWidth = isMobile ? 4 : 6;
    const boxHeight = isMobile ? 3 : 4;
    const questionFontSize = isMobile ? 0.3 : 0.4;
    const bonusFontSize = isMobile ? 0.2 : 0.25;
    const optionFontSize = isMobile ? 0.25 : 0.3;
    const buttonSize = isMobile ? 1.0 : 1.2;
    const yPosition = isMobile ? 5 : 6;
    
    return (
      <group position={[0, yPosition, 0]}>
        {/* Question background */}
        <mesh position={[0, 0, 0]}>
          <boxGeometry args={[boxWidth, boxHeight, 0.1]} />
          <meshStandardMaterial color="#0066ff" transparent opacity={0.8} />
        </mesh>
        
        {/* Question text */}
        <Text
          position={[0, 1.2 * scaleFactor, 0.1]}
          fontSize={questionFontSize}
          color="#ffffff"
          anchorX="center"
          anchorY="middle"
          fontWeight="bold"
        >
          {currentQuestion.question}
        </Text>
        
        {/* Bonus text */}
        <Text
          position={[0, 0.6 * scaleFactor, 0.1]}
          fontSize={bonusFontSize}
          color="#ffff00"
          anchorX="center"
          anchorY="middle"
          fontWeight="bold"
        >
          Answer correctly for +0.1 shooting speed!
        </Text>
        
        {/* Answer options */}
        {currentQuestion.options.map((option, index) => {
          const row = Math.floor(index / 2);
          const col = index % 2;
          const xPos = col === 0 ? -1.5 * scaleFactor : 1.5 * scaleFactor;
          const yPos = row === 0 ? 0 : -0.8 * scaleFactor;
          
          // Determine color based on selection and result
          let buttonColor = "#4a4ae0";
          if (selectedAnswer === option) {
            buttonColor = answerResult === 'correct' ? "#4ae04a" : "#e04a4a";
          }
          
          return (
            <group key={`option-${index}`} position={[xPos, yPos, 0.1]}>
              <mesh 
                position={[0, 0, 0]}
                onClick={(e) => {
                  e.stopPropagation();
                  if (selectedAnswer === null) {
                    handleAnswerSelect(option);
                  }
                }}
              >
                <boxGeometry args={[buttonSize, 0.6, 0.1]} />
                <meshStandardMaterial color={buttonColor} />
              </mesh>
              <Text
                position={[0, 0, 0.1]}
                fontSize={optionFontSize}
                color="#ffffff"
                anchorX="center"
                anchorY="middle"
              >
                {option}
              </Text>
            </group>
          );
        })}
      </group>
    );
  };
  
  // Generate a science question based on tower level
  const generateScienceQuestion = () => {
    // Science questions organized by category
    const scienceQuestions = [
      // General Science
      { 
        question: "What are the three states of matter?", 
        answer: "Solid, liquid, gas", 
        options: ["Solid, liquid, gas", "Hot, cold, warm", "Earth, wind, fire", "Plasma, neutron, electron"],
        category: "General Science"
      },
      { 
        question: "How does the water cycle work?", 
        answer: "Evaporation, condensation, precipitation", 
        options: ["Evaporation, condensation, precipitation", "Melting, freezing, boiling", "Absorption, reflection, refraction", "Osmosis, diffusion, filtration"],
        category: "General Science"
      },
      { 
        question: "What is the scientific method?", 
        answer: "Question, hypothesis, experiment, analyze, conclude", 
        options: ["Question, hypothesis, experiment, analyze, conclude", "Observe, record, calculate, present", "Research, test, prove, publish", "Study, memorize, recite, repeat"],
        category: "General Science"
      },
      { 
        question: "How do plants make their own food?", 
        answer: "Photosynthesis", 
        options: ["Photosynthesis", "Digestion", "Absorption", "Fermentation"],
        category: "General Science"
      },
      { 
        question: "Why do objects fall to the ground when dropped?", 
        answer: "Gravity", 
        options: ["Gravity", "Magnetism", "Electricity", "Air pressure"],
        category: "General Science"
      },
      
      // Biology
      { 
        question: "What are the five kingdoms of living things?", 
        answer: "Animalia, Plantae, Fungi, Protista, Monera", 
        options: ["Animalia, Plantae, Fungi, Protista, Monera", "Mammals, reptiles, birds, fish, insects", "Producers, consumers, decomposers, scavengers, parasites", "Vertebrates, invertebrates, plants, bacteria, viruses"],
        category: "Biology"
      },
      { 
        question: "What is the difference between a plant and an animal cell?", 
        answer: "Plant cells have cell walls and chloroplasts", 
        options: ["Plant cells have cell walls and chloroplasts", "Animal cells are larger than plant cells", "Plant cells have nuclei, animal cells don't", "Animal cells have mitochondria, plant cells don't"],
        category: "Biology"
      },
      { 
        question: "How do animals adapt to their environments?", 
        answer: "Physical and behavioral changes over time", 
        options: ["Physical and behavioral changes over time", "By migrating to new habitats", "Through conscious decision-making", "By eating different foods"],
        category: "Biology"
      },
      { 
        question: "What is the function of the human heart?", 
        answer: "To pump blood throughout the body", 
        options: ["To pump blood throughout the body", "To filter toxins from the blood", "To produce red blood cells", "To regulate body temperature"],
        category: "Biology"
      },
      { 
        question: "Why do we need oxygen to survive?", 
        answer: "For cellular respiration to produce energy", 
        options: ["For cellular respiration to produce energy", "To keep our lungs inflated", "To maintain blood pressure", "To prevent carbon dioxide poisoning"],
        category: "Biology"
      },
      
      // Chemistry
      { 
        question: "What is an atom?", 
        answer: "The basic unit of matter", 
        options: ["The basic unit of matter", "A type of molecule", "A subatomic particle", "A chemical compound"],
        category: "Chemistry"
      },
      { 
        question: "What happens when you mix vinegar and baking soda?", 
        answer: "A chemical reaction producing carbon dioxide gas", 
        options: ["A chemical reaction producing carbon dioxide gas", "They neutralize each other completely", "They form a new liquid", "Nothing happens"],
        category: "Chemistry"
      },
      { 
        question: "What are acids and bases?", 
        answer: "Substances with pH below 7 (acids) or above 7 (bases)", 
        options: ["Substances with pH below 7 (acids) or above 7 (bases)", "Different types of salts", "Elements on the periodic table", "Types of chemical bonds"],
        category: "Chemistry"
      },
      { 
        question: "What is the periodic table used for?", 
        answer: "To organize elements by their properties", 
        options: ["To organize elements by their properties", "To show chemical reactions", "To calculate atomic mass", "To predict weather patterns"],
        category: "Chemistry"
      },
      { 
        question: "How can you tell if a chemical reaction has occurred?", 
        answer: "Color change, gas formation, temperature change, or precipitate formation", 
        options: ["Color change, gas formation, temperature change, or precipitate formation", "The mixture becomes radioactive", "It always creates an explosion", "The substances disappear completely"],
        category: "Chemistry"
      },
      
      // Physics
      { 
        question: "What is Newton's First Law of Motion?", 
        answer: "An object at rest stays at rest unless acted upon by a force", 
        options: ["An object at rest stays at rest unless acted upon by a force", "Force equals mass times acceleration", "For every action there is an equal and opposite reaction", "Energy cannot be created or destroyed"],
        category: "Physics"
      },
      { 
        question: "How does a magnet work?", 
        answer: "It creates a magnetic field that attracts certain metals", 
        options: ["It creates a magnetic field that attracts certain metals", "It generates electricity that pulls objects", "It uses gravity to attract objects", "It changes the density of nearby objects"],
        category: "Physics"
      },
      { 
        question: "What is the difference between potential and kinetic energy?", 
        answer: "Potential is stored energy; kinetic is energy of motion", 
        options: ["Potential is stored energy; kinetic is energy of motion", "Potential is weak energy; kinetic is strong energy", "Potential is in solids; kinetic is in liquids", "Potential is natural; kinetic is man-made"],
        category: "Physics"
      },
      { 
        question: "Why do we see lightning before we hear thunder?", 
        answer: "Light travels faster than sound", 
        options: ["Light travels faster than sound", "Lightning happens before thunder", "Our eyes are more sensitive than our ears", "Thunder takes longer to form than lightning"],
        category: "Physics"
      },
      { 
        question: "How do simple machines make work easier?", 
        answer: "By changing the direction or magnitude of a force", 
        options: ["By changing the direction or magnitude of a force", "By creating energy", "By eliminating the need for force", "By running on electricity"],
        category: "Physics"
      },
      
      // Earth & Space Science
      { 
        question: "Why do we have day and night?", 
        answer: "The Earth rotates on its axis", 
        options: ["The Earth rotates on its axis", "The Sun moves around the Earth", "The Moon blocks the Sun", "The Earth's atmosphere filters light"],
        category: "Earth & Space Science"
      },
      { 
        question: "What causes the seasons?", 
        answer: "The Earth's tilt as it orbits the Sun", 
        options: ["The Earth's tilt as it orbits the Sun", "The Earth's distance from the Sun", "The Moon's gravitational pull", "The rotation of the Earth's core"],
        category: "Earth & Space Science"
      },
      { 
        question: "What are the different types of rocks?", 
        answer: "Igneous, sedimentary, and metamorphic", 
        options: ["Igneous, sedimentary, and metamorphic", "Granite, marble, and limestone", "Hard, soft, and medium", "Volcanic, oceanic, and terrestrial"],
        category: "Earth & Space Science"
      },
      { 
        question: "Why do earthquakes happen?", 
        answer: "Tectonic plates moving against each other", 
        options: ["Tectonic plates moving against each other", "Volcanic eruptions underground", "The Earth's core overheating", "Gravitational pull from the Moon"],
        category: "Earth & Space Science"
      },
      { 
        question: "How do stars form?", 
        answer: "Clouds of gas and dust collapse under gravity", 
        options: ["Clouds of gas and dust collapse under gravity", "Planets that heat up and ignite", "Explosions of other stars", "Moons that break away from planets"],
        category: "Earth & Space Science"
      }
    ];
    
    // Select a random question based on tower level
    const level = tower.level;
    let categoryPool: string[] = [];
    
    if (level === 1) {
      // Level 1: General Science questions
      categoryPool = ["General Science"];
    } else if (level === 2) {
      // Level 2: General Science and Biology
      categoryPool = ["General Science", "Biology"];
    } else if (level === 3) {
      // Level 3: Add Chemistry
      categoryPool = ["General Science", "Biology", "Chemistry"];
    } else if (level === 4) {
      // Level 4: Add Physics
      categoryPool = ["General Science", "Biology", "Chemistry", "Physics"];
    } else {
      // Level 5+: All categories
      categoryPool = ["General Science", "Biology", "Chemistry", "Physics", "Earth & Space Science"];
    }
    
    // Filter questions by the selected categories
    const filteredQuestions = scienceQuestions.filter(q => categoryPool.includes(q.category));
    
    // Select a random question from the filtered list
    const randomIndex = Math.floor(Math.random() * filteredQuestions.length);
    const selectedQuestion = filteredQuestions[randomIndex];
    
    // Create a copy of the question to avoid modifying the original
    const question = { ...selectedQuestion };
    
    // Shuffle options using Fisher-Yates algorithm for better randomization
    const options = [...question.options];
    for (let i = options.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [options[i], options[j]] = [options[j], options[i]];
    }
    
    return {
      ...question,
      options
    };
  };
  
  // Handle science answer selection
  const handleScienceAnswerSelect = (selectedValue: string) => {
    setSelectedScienceAnswer(selectedValue);
    
    if (currentScienceQuestion && selectedValue === currentScienceQuestion.answer) {
      setScienceAnswerResult('correct');
      
      // Wait a moment to show the result, then upgrade the tower
      setTimeout(() => {
        // For science towers, add 0.1 to the worst property
        if (tower.type === 'science') {
          // Determine the worst property (lowest value relative to typical values)
          // Normalize values to compare them fairly
          const normalizedRange = tower.range / 8; // Assuming 8 is a standard range
          const normalizedDamage = tower.damage / 15; // Assuming 15 is a standard damage
          const normalizedFireRate = tower.fireRate / 1; // Assuming 1 is a standard fire rate
          
          // Find the worst property
          let worstProperty: 'range' | 'damage' | 'fireRate';
          
          if (normalizedRange <= normalizedDamage && normalizedRange <= normalizedFireRate) {
            worstProperty = 'range';
          } else if (normalizedDamage <= normalizedRange && normalizedDamage <= normalizedFireRate) {
            worstProperty = 'damage';
          } else {
            worstProperty = 'fireRate';
          }
          
          // Create custom upgrade with 0.1 added to the worst property
          const customUpgrade = {
            ...tower,
            level: tower.level + 1,
            range: tower.range * 1.2, // Standard upgrade
            damage: tower.damage * 1.3, // Standard upgrade
            fireRate: tower.fireRate * 1.1 // Standard upgrade
          };
          
          // Add 0.1 to the worst property
          if (worstProperty === 'range') {
            customUpgrade.range += 0.1;
            setBoostedProperty('Range');
          } else if (worstProperty === 'damage') {
            customUpgrade.damage += 0.1;
            setBoostedProperty('Damage');
          } else {
            customUpgrade.fireRate += 0.1;
            setBoostedProperty('Fire Rate');
          }
          
          upgradeTower(tower.id, customUpgrade);
          
          // Show property boost effect
          setShowPropertyBoost(true);
          console.log("Setting showPropertyBoost to true for tower:", tower.id);
          
          // Clear any existing timeout
          if (propertyBoostRef.current) {
            clearTimeout(propertyBoostRef.current);
          }
          
          // Hide the effect after 3 seconds
          propertyBoostRef.current = window.setTimeout(() => {
            setShowPropertyBoost(false);
            propertyBoostRef.current = null;
            console.log("Setting showPropertyBoost to false for tower:", tower.id);
          }, 3000);
        } else {
          upgradeTower(tower.id);
        }
        resetScienceQuestion();
      }, 1000);
    } else {
      setScienceAnswerResult('incorrect');
      
      // Wait a moment to show the result, then reset
      setTimeout(() => {
        resetScienceQuestion();
      }, 1000);
    }
  };
  
  // Reset science question state
  const resetScienceQuestion = () => {
    setShowScienceQuestion(false);
    setCurrentScienceQuestion(null);
    setSelectedScienceAnswer(null);
    setScienceAnswerResult(null);
  };
  
  // Render science question UI with responsive sizing
  const renderScienceQuestion = () => {
    if (!showScienceQuestion || !currentScienceQuestion) return null;
    
    // Determine the worst property (lowest value relative to typical values)
    const normalizedRange = tower.range / 8; // Assuming 8 is a standard range
    const normalizedDamage = tower.damage / 15; // Assuming 15 is a standard damage
    const normalizedFireRate = tower.fireRate / 1; // Assuming 1 is a standard fire rate
    
    let worstProperty: 'range' | 'damage' | 'fireRate';
    
    if (normalizedRange <= normalizedDamage && normalizedRange <= normalizedFireRate) {
      worstProperty = 'range';
    } else if (normalizedDamage <= normalizedRange && normalizedDamage <= normalizedFireRate) {
      worstProperty = 'damage';
    } else {
      worstProperty = 'fireRate';
    }
    
    // Get display name for the property
    const propertyDisplayName = 
      worstProperty === 'range' ? 'range' : 
      worstProperty === 'damage' ? 'damage' : 
      'fire rate';
    
    // Adjust sizes for mobile
    const boxWidth = isMobile ? 6 : 8;
    const boxHeight = isMobile ? 4 : 5;
    const categoryFontSize = isMobile ? 0.25 : 0.3;
    const questionFontSize = isMobile ? 0.3 : 0.35;
    const bonusFontSize = isMobile ? 0.2 : 0.25;
    const optionFontSize = isMobile ? 0.2 : 0.25;
    const buttonWidth = isMobile ? 2.8 : 3.5;
    const yPosition = isMobile ? 5 : 6;
    
    return (
      <group position={[0, yPosition, 0]}>
        {/* Question background */}
        <mesh position={[0, 0, 0]}>
          <boxGeometry args={[boxWidth, boxHeight, 0.1]} />
          <meshStandardMaterial color="#00aa44" transparent opacity={0.8} />
        </mesh>
        
        {/* Category text */}
        <Text
          position={[0, 1.8 * scaleFactor, 0.1]}
          fontSize={categoryFontSize}
          color="#ffff00"
          anchorX="center"
          anchorY="middle"
          fontWeight="bold"
        >
          {currentScienceQuestion.category}
        </Text>
        
        {/* Question text */}
        <Text
          position={[0, 1.2 * scaleFactor, 0.1]}
          fontSize={questionFontSize}
          color="#ffffff"
          anchorX="center"
          anchorY="middle"
          fontWeight="bold"
          maxWidth={boxWidth - 1}
        >
          {currentScienceQuestion.question}
        </Text>
        
        {/* Bonus text */}
        <Text
          position={[0, 0.6 * scaleFactor, 0.1]}
          fontSize={bonusFontSize}
          color="#ffff00"
          anchorX="center"
          anchorY="middle"
          fontWeight="bold"
        >
          Answer correctly for +0.1 {propertyDisplayName}!
        </Text>
        
        {/* Answer options */}
        {currentScienceQuestion.options.map((option, index) => {
          const row = Math.floor(index / 2);
          const col = index % 2;
          const xPos = col === 0 ? -2.5 * scaleFactor : 2.5 * scaleFactor;
          const yPos = row === 0 ? 0 : -0.8 * scaleFactor;
          
          // Determine color based on selection and result
          let buttonColor = "#4a4ae0";
          if (selectedScienceAnswer === option) {
            buttonColor = scienceAnswerResult === 'correct' ? "#4ae04a" : "#e04a4a";
          }
          
          return (
            <group key={`option-${index}`} position={[xPos, yPos, 0.1]}>
              <mesh 
                position={[0, 0, 0]}
                onClick={(e) => {
                  e.stopPropagation();
                  if (selectedScienceAnswer === null) {
                    handleScienceAnswerSelect(option);
                  }
                }}
              >
                <boxGeometry args={[buttonWidth, 0.6, 0.1]} />
                <meshStandardMaterial color={buttonColor} />
              </mesh>
              <Text
                position={[0, 0, 0.1]}
                fontSize={optionFontSize}
                color="#ffffff"
                anchorX="center"
                anchorY="middle"
                maxWidth={buttonWidth - 0.3}
              >
                {option}
              </Text>
            </group>
          );
        })}
      </group>
    );
  };
  
  // Handle joke submission for drama towers (previously for art towers)
  const handleJokeSubmit = (joke: string) => {
    console.log(`Handling joke submission for tower ${tower.id}: ${joke}`);
    
    try {
      // Force turn off any existing effect first
      setShowJokeEffect(false);
      
      // Create updated jokes array
      const updatedJokes = [...(tower.jokes || []), joke];
      
      // Store the joke in the tower
      const updatedTower = {
        ...tower,
        jokes: updatedJokes,
        level: tower.level + 1,
        range: tower.range * 1.2,
        damage: tower.damage * 1.3,
        fireRate: tower.fireRate * 1.1
      };
      
      console.log("Upgrading tower with joke", updatedTower);
      
      // Save jokes to localStorage
      saveJokesToStorage(tower.id, updatedJokes);
      
      // Apply the upgrade with the new joke
      upgradeTower(tower.id, updatedTower);
      
      // Set the debug flag (this will always show the debug visualization)
      setDebugJokeEffect(true);
      
      // Force show the joke effect with a small delay to ensure state updates
      setTimeout(() => {
        console.log("Forcing joke effect to show");
        setShowJokeEffect(true);
        setCurrentJoke(joke);
        setIsShowingJoke(true);
        setJokeDisplayTime(0);
        
        // Hide the effect after 5 seconds
        if (jokeEffectRef.current) {
          clearTimeout(jokeEffectRef.current);
        }
        
        jokeEffectRef.current = window.setTimeout(() => {
          console.log("Hiding joke effect after timeout");
          setShowJokeEffect(false);
          setDebugJokeEffect(false);
          jokeEffectRef.current = null;
        }, 5000);
      }, 50);
      
      // If this tower is selected, update the selected tower with the upgrades
      if (selectedTower && selectedTower.id === tower.id) {
        console.log("Updating selected tower reference after joke upgrade");
        // Re-select the tower to update the UI
        selectTower(null);
        setTimeout(() => selectTower(tower.id), 100);
      }
      
      console.log("Joke submission handler completed successfully");
    } catch (error) {
      console.error("Error in joke submission handler:", error);
    }
  };
  
  // Function to tell a random joke (for drama towers)
  const tellJoke = () => {
    if (tower.jokes && tower.jokes.length > 0) {
      // Pick a random joke from the tower's collection
      const randomIndex = Math.floor(Math.random() * tower.jokes.length);
      const joke = tower.jokes[randomIndex];
      
      // Show the joke
      setCurrentJoke(joke);
      setIsShowingJoke(true);
      // Use current time as a reference point
      setJokeDisplayTime(0); // Will be set in useFrame
      
      // Upgrade the tower when telling a joke
      const updatedTower = {
        ...tower,
        level: tower.level + 1,
        range: tower.range * 1.2,
        damage: tower.damage * 1.3,
        fireRate: tower.fireRate * 1.1
      };
      
      // Apply the upgrade
      upgradeTower(tower.id, updatedTower);
      
      // Show joke effect
      setShowJokeEffect(true);
      
      // Also set debug flag to ensure visibility
      setDebugJokeEffect(true);
      
      console.log("Telling joke, showing effects");
      
      // Clear any existing timeout
      if (jokeEffectRef.current) {
        clearTimeout(jokeEffectRef.current);
      }
      
      // Hide the effect after 5 seconds
      jokeEffectRef.current = window.setTimeout(() => {
        console.log("Hiding joke effects after timeout");
        setShowJokeEffect(false);
        setDebugJokeEffect(false);
        jokeEffectRef.current = null;
      }, 5000);
    }
  };
  
  // Register the handleJokeSubmit function for drama towers
  useEffect(() => {
    console.log(`Registering joke handler for tower ${tower.id} (type: ${tower.type})`);
    if (tower.type === 'drama') {  // Changed from 'art' to 'drama'
      // Register the joke submit handler
      setJokeSubmitHandler(tower.id, handleJokeSubmit);

      // Load any saved jokes from localStorage for this tower
      const savedJokes = getJokesFromStorage(tower.id);
      if (savedJokes.length > 0 && (!tower.jokes || tower.jokes.length === 0)) {
        console.log(`Loading ${savedJokes.length} saved jokes for tower ${tower.id}`);
        // Update the tower with saved jokes
        upgradeTower(tower.id, {
          ...tower,
          jokes: savedJokes
        });
      }
    }
    
    return () => {
      // Clean up
      if (tower.type === 'drama') {
        clearJokeSubmitHandler(tower.id);
      }
    };
  }, [tower.id, tower.type]); // Removed handleJokeSubmit from dependencies to avoid recreation
  
  // Register the handleDrawingSubmit function for art towers
  useEffect(() => {
    if (tower.type === 'art') {
      // Register the drawing submit handler
      setDrawingSubmitHandler(tower.id, handleDrawingSubmit);
    }
    
    return () => {
      // Clean up
      if (tower.type === 'art') {
        clearDrawingSubmitHandler(tower.id);
      }
    };
  }, [tower.id, tower.type, handleDrawingSubmit]);

  // Activate art tower special ability - now uses drawing canvas
  const activateArtSpecialAbility = () => {
    console.log('Activating art tower special ability for tower:', tower.id);
    // Show the drawing canvas
    const prompt = generateDrawingPrompt();
    console.log('Generated drawing prompt:', prompt);
    console.log('Drawing canvas context:', { showDrawingCanvas });
    showDrawingCanvas(tower.id, prompt);
  };
  
  // Activate drama tower special ability - now uses joke input
  const activateDramaSpecialAbility = () => {
    // Show the joke input
    showJokeInput(tower.id);
  };
  
  // Render joke bubble with responsive sizing
  const renderJokeBubble = () => {
    if (!isShowingJoke || !currentJoke) return null;
    
    const bubbleWidth = isMobile ? 4 : 6;
    const bubbleHeight = isMobile ? 2 : 2.5;
    const fontSize = isMobile ? 0.25 : 0.3;
    const yPosition = isMobile ? 4 : 5;
    
    return (
      <group position={[tower.position.x, tower.position.y + yPosition, tower.position.z]}>
        {/* Speech bubble background */}
        <mesh position={[0, 0, 0]}>
          <boxGeometry args={[bubbleWidth, bubbleHeight, 0.1]} />
          <meshStandardMaterial color="#ffffff" transparent opacity={0.9} />
        </mesh>
        
        {/* Joke text */}
        <Text
          position={[0, 0, 0.1]}
          fontSize={fontSize}
          color="#000000"
          anchorX="center"
          anchorY="middle"
          maxWidth={bubbleWidth - 1}
          textAlign="center"
        >
          {currentJoke}
        </Text>
        
        {/* Speech bubble pointer */}
        <mesh position={[0, -1.3 * scaleFactor, 0]} rotation={[0, 0, Math.PI / 4]}>
          <boxGeometry args={[0.7, 0.7, 0.1]} />
          <meshStandardMaterial color="#ffffff" transparent opacity={0.9} />
        </mesh>
      </group>
    );
  };
  
  // Render property boost effect with responsive sizing
  const renderPropertyBoost = () => {
    if (!showPropertyBoost) return null;
    
    console.log("Rendering property boost effect for:", boostedProperty);
    
    const circleSize = isMobile ? 1.5 : 2;
    const textSize = isMobile ? 0.5 : 0.7;
    const yPosition = isMobile ? 2.5 : 3;
    
    return (
      <group position={[tower.position.x, tower.position.y + yPosition, tower.position.z]}>
        {/* Yellow circle */}
        <mesh position={[0, 0, 0]} rotation={[Math.PI / 2, 0, 0]}>
          <circleGeometry args={[circleSize, 32]} />
          <meshBasicMaterial color="#ffff00" transparent opacity={0.7} />
        </mesh>
        
        {/* Yellow ring */}
        <mesh position={[0, 0.1, 0]} rotation={[Math.PI / 2, 0, 0]}>
          <ringGeometry args={[circleSize * 0.9, circleSize * 1.1, 32]} />
          <meshBasicMaterial color="#ffcc00" transparent opacity={0.9} />
        </mesh>
        
        {/* Starburst effect */}
        {[...Array(8)].map((_, i) => (
          <mesh 
            key={`starburst-${i}`} 
            position={[
              Math.sin(i * Math.PI / 4) * circleSize * 1.25, 
              0.2, 
              Math.cos(i * Math.PI / 4) * circleSize * 1.25
            ]}
            rotation={[Math.PI / 2, 0, i * Math.PI / 4]}
          >
            <planeGeometry args={[0.5 * scaleFactor, 1.5 * scaleFactor]} />
            <meshBasicMaterial color="#ffff00" transparent opacity={0.8} />
          </mesh>
        ))}
        
        {/* Property boost text */}
        <Billboard
          position={[0, circleSize, 0]}
          follow={true}
        >
          <mesh position={[0, 0, -0.01]}>
            <planeGeometry args={[3.0 * scaleFactor, 1.0 * scaleFactor]} />
            <meshBasicMaterial color="black" transparent opacity={0.7} />
          </mesh>
          <Text
            position={[0, 0, 0]}
            fontSize={textSize}
            color="#ffff00"
            anchorX="center"
            anchorY="middle"
            fontWeight="bold"
          >
            +0.1 {boostedProperty}!
          </Text>
        </Billboard>
      </group>
    );
  };
  
  // Add a debug log to verify the effect is being triggered
  useEffect(() => {
    if (showPropertyBoost) {
      console.log("Property boost effect activated:", boostedProperty);
    }
  }, [showPropertyBoost, boostedProperty]);
  
  // Make sure we still animate the property boost effect
  useFrame((state) => {
    if (showPropertyBoost) {
      propertyBoostTimeRef.current = state.clock.getElapsedTime();
    }
  });

  // Add this useEffect for upgrade button
  useEffect(() => {
    if (upgradeButtonClicked) {
      // Set initial animation state
      setUpgradeButtonScale(0.95); // Less dramatic scale for better responsiveness
      setUpgradeFlashOpacity(0.8);
      
      // Return to normal after animation
      const timer = setTimeout(() => {
        setUpgradeButtonClicked(false);
        setUpgradeButtonScale(1);
        setUpgradeFlashOpacity(0);
      }, 100); // Reduced from 300ms to 100ms for faster response
      
      return () => clearTimeout(timer);
    }
  }, [upgradeButtonClicked]);

  // Add this useEffect for sell button
  useEffect(() => {
    if (sellButtonClicked) {
      // Set initial animation state
      setSellButtonScale(0.85); // Smaller scale for more obvious feedback
      setSellFlashOpacity(0.8); // Brighter flash
      setSellButtonDisabled(true); // Disable button during animation
      
      // Return to normal after animation
      const timer = setTimeout(() => {
        setSellButtonClicked(false);
        setSellButtonScale(1);
        setSellFlashOpacity(0);
        // Re-enable button after a short delay to prevent accidental double clicks
        setTimeout(() => setSellButtonDisabled(false), 100);
      }, 200); // Faster animation time (was 300ms)
      
      return () => clearTimeout(timer);
    }
  }, [sellButtonClicked]);

  // Add this useEffect for joke button
  useEffect(() => {
    if (jokeButtonClicked) {
      // Set initial animation state
      setJokeButtonScale(0.95); // Even less dramatic scale for better responsiveness
      setJokeFlashOpacity(0.8); // Keep bright flash for feedback
      setJokeButtonDisabled(true); // Disable button during animation
      
      // Return to normal after animation
      const timer = setTimeout(() => {
        setJokeButtonClicked(false);
        setJokeButtonScale(1);
        setJokeFlashOpacity(0);
        // Re-enable button after a very short delay to prevent accidental double clicks
        setTimeout(() => setJokeButtonDisabled(false), 25); // Reduced from 50ms to 25ms
      }, 50); // Reduced from 100ms to 50ms for faster response
      
      return () => clearTimeout(timer);
    }
  }, [jokeButtonClicked]);

  // Update the useFrame hook to handle all buttons
  useFrame((state, delta) => {
    // Animate the upgrade button when hovered
    if (upgradeButtonHovered && !upgradeButtonClicked) {
      // Faster rotation for more responsive feel
      setUpgradeButtonRotation(state => state + delta * 1.0);
      setUpgradeButtonScale(1 + Math.sin(state.clock.elapsedTime * 4) * 0.03); // Reduced scale change
    }
    
    // Animate the sell button when hovered
    if (sellButtonHovered && !sellButtonClicked) {
      setSellButtonRotation(state => state + delta * 0.5);
      setSellButtonScale(1 + Math.sin(state.clock.elapsedTime * 3) * 0.05);
    }
    
    // Animate the joke button when hovered
    if (jokeButtonHovered && !jokeButtonClicked) {
      setJokeButtonRotation(state => state + delta * 0.5);
      setJokeButtonScale(1 + Math.sin(state.clock.elapsedTime * 3) * 0.05);
    }
    
    // Decrease flash opacity over time when clicked
    if (upgradeButtonClicked && upgradeFlashOpacity > 0) {
      setUpgradeFlashOpacity(current => Math.max(0, current - delta * 4)); // Faster fade out
    }
    
    if (sellButtonClicked && sellFlashOpacity > 0) {
      setSellFlashOpacity(current => Math.max(0, current - delta * 2));
    }
    
    if (jokeButtonClicked && jokeFlashOpacity > 0) {
      setJokeFlashOpacity(current => Math.max(0, current - delta * 2));
    }
  });

  // Add a simple debug flag for joke effect visualization
  const [debugJokeEffect, setDebugJokeEffect] = useState(false);
  
  // Animate the art effect
  useFrame((state) => {
    if (showArtEffect) {
      artEffectTimeRef.current = state.clock.getElapsedTime();
    }
  });
  
  return (
    <>
      {/* World position visualization - REMOVED to hide floating coordinates */}
      
      {/* Shooting effects - rendered outside the tower group */}
      {renderShootingEffects()}
      
      {/* Enemy position labels - REMOVED to hide floating coordinates */}
      
      {/* Math question UI - rendered outside the tower group */}
      {renderMathQuestion()}
      
      {/* Science question UI - rendered outside the tower group */}
      {renderScienceQuestion()}
      
      {/* Property boost effect - rendered outside the tower group */}
      {renderPropertyBoost()}
      
      {/* Joke bubble - rendered outside the tower group */}
      {tower.type === 'drama' && renderJokeBubble()}
      
      <group 
        position={tower.position} 
        onClick={handleClick}
        onPointerOver={() => setHovered(true)}
        onPointerOut={() => setHovered(false)}
      >
        {/* Tower position label - REMOVED to hide floating coordinates */}
        
        {/* Debug button for science towers - REMOVED */}
        
        {/* Tower base - only for non-math towers */}
        {tower.type !== 'math' && (
          <mesh position={[0, 0.5, 0]} castShadow receiveShadow>
            <cylinderGeometry args={[0.8 * scaleFactor, 1 * scaleFactor, 1, 16]} />
            <meshStandardMaterial 
              color={isSelected ? '#ffffff' : hovered ? '#aaaaaa' : colors.base} 
            />
          </mesh>
        )}
        
        {/* Tower top - rotates */}
        <group ref={topRef} scale={[scaleFactor, scaleFactor, scaleFactor]}>
          {renderTowerTop()}
        </group>
        
        {/* Visualization of calculated wand tip position (local) - REMOVED to hide coordinates */}
        
        {/* Tower level indicator */}
        <Text
          position={[0, 0, 0]}
          rotation={[0, 0, 0]}
          fontSize={0.5 * scaleFactor}
          color="#ffffff"
          anchorX="center"
          anchorY="middle"
        >
          {tower.level}
        </Text>
        
        {/* Fire rate indicator for math towers */}
        {tower.type === 'math' && (
          <Text
            position={[0, -0.5, 0]}
            rotation={[0, 0, 0]}
            fontSize={0.3 * scaleFactor}
            color="#ffffff"
            anchorX="center"
            anchorY="middle"
          >
            {`Speed: ${tower.fireRate.toFixed(1)}`}
          </Text>
        )}
        
        {/* Speed boost visual effect */}
        {tower.type === 'math' && showSpeedBoost && (
          <group>
            {/* Pulsating speed boost aura */}
            <mesh position={[0, 1.0, 0]}>
              <sphereGeometry args={[(1.5 + Math.sin(speedBoostTimeRef.current * 5) * 0.2) * scaleFactor, 16, 16]} />
              <meshStandardMaterial 
                color="#ffff00" 
                transparent 
                opacity={0.3 + Math.sin(speedBoostTimeRef.current * 3) * 0.1} 
                emissive="#ffff00"
                emissiveIntensity={0.5}
              />
            </mesh>
            
            {/* Speed boost text */}
            <Billboard
              position={[0, 3.0 * scaleFactor, 0]}
              follow={true}
              lockX={false}
              lockY={false}
              lockZ={false}
            >
              <Text
                position={[0, 0, 0 + Math.sin(speedBoostTimeRef.current * 4) * 0.1]}
                fontSize={(0.4 + Math.sin(speedBoostTimeRef.current * 2) * 0.05) * scaleFactor}
                color="#ffff00"
                anchorX="center"
                anchorY="middle"
                fontWeight="bold"
              >
                SPEED BOOST!
              </Text>
            </Billboard>
            
            {/* Speed lines */}
            {Array.from({ length: 8 }).map((_, i) => {
              const angle = (i / 8) * Math.PI * 2 + speedBoostTimeRef.current;
              const radius = 1.2 * scaleFactor;
              return (
                <mesh 
                  key={`speedline-${i}`}
                  position={[
                    Math.cos(angle) * radius,
                    1.0,
                    Math.sin(angle) * radius
                  ]}
                  rotation={[0, -angle, 0]}
                >
                  <planeGeometry args={[0.1 * scaleFactor, 0.5 * scaleFactor]} />
                  <meshStandardMaterial 
                    color="#ffff00" 
                    transparent 
                    opacity={0.7} 
                    emissive="#ffff00"
                    emissiveIntensity={0.8}
                  />
                </mesh>
              );
            })}
          </group>
        )}
        
        {/* Range indicator (only when selected) */}
        {isSelected && (
          <mesh position={[0, 0.05, 0]} rotation={[-Math.PI / 2, 0, 0]}>
            <ringGeometry args={[tower.range - 0.1, tower.range, 64]} />
            <meshStandardMaterial color="#ffffff" transparent opacity={0.2} />
          </mesh>
        )}
        
        {/* Tower controls when selected */}
        {isSelected && (
          <group position={[0, 4 * scaleFactor, 0]}>
            {/* Upgrade button */}
            <group 
              position={[-1 * scaleFactor, 0, 0]} 
              rotation={[0, upgradeButtonRotation, 0]}
              scale={[upgradeButtonScale, upgradeButtonScale, upgradeButtonScale]}
            >
              <mesh 
                ref={upgradeButtonRef}
                onClick={(e) => {
                  e.stopPropagation();
                  
                  // Visual feedback on click
                  setUpgradeButtonClicked(true);
                  
                  console.log('Tower upgrade button clicked for type:', tower.type);
                  
                  // For math towers, show a math question
                  if (tower.type === 'math') {
                    const question = generateMathQuestion();
                    setCurrentQuestion(question);
                    setShowMathQuestion(true);
                  } 
                  // For science towers, show a science question
                  else if (tower.type === 'science') {
                    const question = generateScienceQuestion();
                    setCurrentScienceQuestion(question);
                    setShowScienceQuestion(true);
                  }
                  // For art towers, show drawing canvas (swapped with drama)
                  else if (tower.type === 'art') {
                    console.log('Art tower clicked, activating special ability');
                    activateArtSpecialAbility();
                  }
                  // For drama towers, show joke input (swapped with art)
                  else if (tower.type === 'drama') {
                    activateDramaSpecialAbility();
                  }
                  else {
                    // For other towers, upgrade directly
                    upgradeTower(tower.id);
                  }
                }}
                onPointerOver={(e) => {
                  e.stopPropagation();
                  setUpgradeButtonHovered(true);
                  document.body.style.cursor = 'pointer';
                }}
                onPointerOut={(e) => {
                  e.stopPropagation();
                  setUpgradeButtonHovered(false);
                  document.body.style.cursor = 'auto';
                  // Reset scale and rotation when not hovered
                  setUpgradeButtonScale(1);
                  setUpgradeButtonRotation(0);
                }}
              >
                <boxGeometry args={[0.8 * scaleFactor, 0.8 * scaleFactor, 0.8 * scaleFactor]} />
                <meshStandardMaterial 
                  color={upgradeButtonHovered ? "#6a6af0" : "#4a4ae0"} 
                  emissive={upgradeButtonHovered ? "#6a6af0" : "#000000"}
                  emissiveIntensity={upgradeButtonHovered ? 0.5 : 0}
                  roughness={0.5}
                  metalness={0.8}
                />
              </mesh>
              
              {/* Flash effect on click */}
              {upgradeFlashOpacity > 0 && (
                <mesh scale={[1.2, 1.2, 1.2]}>
                  <sphereGeometry args={[0.5 * scaleFactor, 8, 8]} />
                  <meshBasicMaterial color="#ffffff" transparent opacity={upgradeFlashOpacity} />
                </mesh>
              )}
              
              <Text
                position={[0, 0, 0.5 * scaleFactor]}
                fontSize={upgradeButtonHovered ? 0.35 * scaleFactor : 0.3 * scaleFactor}
                color="#ffffff"
                anchorX="center"
                anchorY="middle"
                fillOpacity={upgradeButtonHovered ? 1 : 0.9}
              >
                UP
              </Text>
            </group>
            
            {/* Tell Joke button (only for drama towers with jokes) */}
            {tower.type === 'drama' && tower.jokes && tower.jokes.length > 0 && (
              <>
                <group 
                  position={[1 * scaleFactor, 0, 0]} 
                  rotation={[0, jokeButtonRotation, 0]}
                  scale={[jokeButtonScale, jokeButtonScale, jokeButtonScale]}
                >
                  <mesh 
                    ref={jokeButtonRef}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (jokeButtonDisabled) return; // Prevent clicking if disabled
                      setJokeButtonClicked(true);
                      tellJoke();
                    }}
                    onPointerOver={(e) => {
                      e.stopPropagation();
                      setJokeButtonHovered(true);
                      document.body.style.cursor = 'pointer';
                      // Add immediate visual feedback on hover
                      setJokeButtonScale(1.1); // Reduced from 1.15 for more subtle hover effect
                    }}
                    onPointerOut={(e) => {
                      e.stopPropagation();
                      setJokeButtonHovered(false);
                      document.body.style.cursor = 'auto';
                      // Reset scale and rotation when not hovered
                      setJokeButtonScale(1);
                      setJokeButtonRotation(0);
                    }}
                  >
                    {/* Reduce the size of the button hitbox to match other buttons */}
                    <boxGeometry args={[0.8 * scaleFactor, 0.8 * scaleFactor, 0.8 * scaleFactor]} />
                    <meshStandardMaterial 
                      color={jokeButtonHovered ? "#6af06a" : "#4ae04a"} 
                      emissive={jokeButtonHovered ? "#6af06a" : "#000000"}
                      emissiveIntensity={jokeButtonHovered ? 0.5 : 0}
                      roughness={0.5}
                      metalness={0.8}
                    />
                  </mesh>
                  
                  {/* Flash effect on click */}
                  {jokeFlashOpacity > 0 && (
                    <mesh scale={[1.2, 1.2, 1.2]}>
                      <sphereGeometry args={[0.5 * scaleFactor, 8, 8]} />
                      <meshBasicMaterial color="#ffffff" transparent opacity={jokeFlashOpacity} />
                    </mesh>
                  )}
                  
                  <Text
                    position={[0, 0, 0.5 * scaleFactor]}
                    fontSize={jokeButtonHovered ? 0.35 * scaleFactor : 0.3 * scaleFactor}
                    color="#ffffff"
                    anchorX="center"
                    anchorY="middle"
                    fillOpacity={jokeButtonHovered ? 1 : 0.9}
                  >
                    JOKE
                  </Text>
                </group>
              </>
            )}
            
            {/* Sell button */}
            <group 
              position={[0, 0, 0]} 
              rotation={[0, sellButtonRotation, 0]}
              scale={[sellButtonScale, sellButtonScale, sellButtonScale]}
            >
              <mesh 
                ref={sellButtonRef}
                onClick={(e) => {
                  e.stopPropagation();
                  if (sellButtonDisabled) return; // Prevent clicking if disabled
                  setSellButtonClicked(true);
                  sellTower(tower.id);
                }}
                onPointerOver={(e) => {
                  e.stopPropagation();
                  setSellButtonHovered(true);
                  document.body.style.cursor = 'pointer';
                  // Add immediate visual feedback on hover
                  setSellButtonScale(1.05);
                }}
                onPointerOut={(e) => {
                  e.stopPropagation();
                  setSellButtonHovered(false);
                  document.body.style.cursor = 'auto';
                  // Reset scale and rotation when not hovered
                  setSellButtonScale(1);
                  setSellButtonRotation(0);
                }}
              >
                {/* Increase the size of the button hitbox slightly for better responsiveness */}
                <boxGeometry args={[0.9 * scaleFactor, 0.9 * scaleFactor, 0.9 * scaleFactor]} />
                <meshStandardMaterial 
                  color={sellButtonHovered ? "#ff6b6b" : "#e04a4a"} 
                  emissive={sellButtonHovered ? "#ff6b6b" : "#000000"}
                  emissiveIntensity={sellButtonHovered ? 0.5 : 0}
                  roughness={0.5}
                  metalness={0.8}
                />
              </mesh>
              
              {/* Flash effect on click */}
              {sellFlashOpacity > 0 && (
                <mesh scale={[1.2, 1.2, 1.2]}>
                  <sphereGeometry args={[0.5 * scaleFactor, 8, 8]} />
                  <meshBasicMaterial color="#ffffff" transparent opacity={sellFlashOpacity} />
                </mesh>
              )}
              
              <Text
                position={[0, 0, 0.5 * scaleFactor]}
                fontSize={sellButtonHovered ? 0.35 * scaleFactor : 0.3 * scaleFactor}
                color="#ffffff"
                anchorX="center"
                anchorY="middle"
                fillOpacity={sellButtonHovered ? 1 : 0.9}
              >
                SELL
              </Text>
            </group>
          </group>
        )}
      </group>
      
      {/* Drama effect visual */}
      {/* Removed drama tower aura effect */}
      
      {/* Drama joke effect visual - SIMPLIFIED VERSION */}
      {tower.type === 'drama' && showJokeEffect && (
        <group position={tower.position}>
          {/* Static joke aura */}
          <mesh position={[0, 1.0, 0]}>
            <sphereGeometry args={[1.8 * scaleFactor, 16, 16]} />
            <meshStandardMaterial 
              color="#9932cc" 
              transparent 
              opacity={0.4} 
              emissive="#9932cc"
              emissiveIntensity={0.8}
            />
          </mesh>
          
          {/* Joke effect text */}
          <Billboard
            position={[0, 3.0 * scaleFactor, 0]}
            follow={true}
            lockX={false}
            lockY={false}
            lockZ={false}
          >
            <mesh>
              <planeGeometry args={[3 * scaleFactor, 1 * scaleFactor]} />
              <meshBasicMaterial color="#9932cc" transparent opacity={0.7} />
            </mesh>
            <Text
              position={[0, 0, 0.1]}
              fontSize={0.4 * scaleFactor}
              color="#ffffff"
              anchorX="center"
              anchorY="middle"
              fontWeight="bold"
            >
              NEW JOKE LEARNED!
            </Text>
          </Billboard>
          
          {/* Static laugh symbols */}
          {["HA", "HE", "LOL", "😂", "🤣", "😆", "HO", "HI"].map((symbol, i) => {
            const angle = (i / 8) * Math.PI * 2;
            const radius = 1.5 * scaleFactor;
            const height = 0.8 + (i % 3) * 0.3;
            return (
              <Billboard 
                key={`jokesymbol-${i}`}
                position={[
                  Math.cos(angle) * radius,
                  height,
                  Math.sin(angle) * radius
                ]}
                follow={true}
              >
                <Text
                  position={[0, 0, 0]}
                  fontSize={0.4 * scaleFactor}
                  color="#ffffff"
                  anchorX="center"
                  anchorY="middle"
                  fontWeight="bold"
                >
                  {symbol}
                </Text>
              </Billboard>
            );
          })}
        </group>
      )}
      
      {/* SUPER BRIGHT DEBUG VISUALIZATION - Always appears when debugJokeEffect is true */}
      {tower.type === 'drama' && debugJokeEffect && (
        <group position={tower.position}>
          {/* Add animation-free large glow sphere */}
          <mesh position={[0, 3.0 * scaleFactor, 0]}>
            <sphereGeometry args={[2.5 * scaleFactor, 32, 32]} />
            <meshBasicMaterial color="#ff0000" transparent opacity={0.4} />
          </mesh>
          
          <Billboard 
            position={[0, 5.0 * scaleFactor, 0]} 
            follow={true}
          >
            <mesh>
              <boxGeometry args={[5 * scaleFactor, 1.5 * scaleFactor, 0.1]} />
              <meshBasicMaterial color="#ff0000" />
            </mesh>
            <Text
              position={[0, 0, 0.1]}
              fontSize={0.8 * scaleFactor}
              color="white"
              anchorX="center"
              anchorY="middle"
              fontWeight="bold"
            >
              TOWER UPGRADED!
            </Text>
          </Billboard>
          
          {/* Add directional arrows pointing to the tower */}
          {[0, 1, 2, 3].map((i) => {
            const angle = (i * Math.PI / 2);
            return (
              <Billboard
                key={`arrow-${i}`}
                position={[
                  Math.sin(angle) * 3 * scaleFactor,
                  3.0 * scaleFactor,
                  Math.cos(angle) * 3 * scaleFactor
                ]}
                follow={true}
              >
                <Text
                  position={[0, 0, 0]}
                  fontSize={1.0 * scaleFactor}
                  color="#ff0000"
                  anchorX="center"
                  anchorY="middle"
                  fontWeight="bold"
                >
                  ↓
                </Text>
              </Billboard>
            );
          })}
        </group>
      )}
      
      {/* Art tower effect */}
      {tower.type === 'art' && showArtEffect && (
        <group position={tower.position}>
          {/* Pulsating art aura */}
          <mesh position={[0, 1.0, 0]}>
            <sphereGeometry args={[(2.0 + Math.sin(artEffectTimeRef.current * 5) * 0.3) * scaleFactor, 32, 32]} />
            <meshStandardMaterial 
              color="#4ae04a" 
              transparent 
              opacity={0.5 + Math.sin(artEffectTimeRef.current * 3) * 0.2} 
              emissive="#4ae04a"
              emissiveIntensity={1.0}
            />
          </mesh>
          
          {/* Art effect text */}
          <Billboard
            position={[0, 3.0 * scaleFactor, 0]}
            follow={true}
            lockX={false}
            lockY={false}
            lockZ={false}
          >
            <Text
              position={[0, 0, 0 + Math.sin(artEffectTimeRef.current * 4) * 0.1]}
              fontSize={(0.4 + Math.sin(artEffectTimeRef.current * 2) * 0.05) * scaleFactor}
              color="#ffffff"
              anchorX="center"
              anchorY="middle"
              fontWeight="bold"
            >
              ARTISTIC BOOST!
            </Text>
          </Billboard>
          
          {/* Floating art symbols */}
          {["🎨", "🖌️", "🎭", "🎪", "🎯", "🎪", "🎨", "🖼️"].map((symbol, i) => {
            const angle = (i / 8) * Math.PI * 2 + artEffectTimeRef.current;
            const radius = 1.2 * scaleFactor;
            return (
              <Billboard 
                key={`artsymbol-${i}`}
                position={[
                  Math.cos(angle) * radius,
                  1.0 + Math.sin(artEffectTimeRef.current * 2 + i) * 0.3,
                  Math.sin(angle) * radius
                ]}
                follow={true}
              >
                <Text
                  position={[0, 0, 0]}
                  fontSize={0.4 * scaleFactor}
                  color="#ffffff"
                  anchorX="center"
                  anchorY="middle"
                  fontWeight="bold"
                >
                  {symbol}
                </Text>
              </Billboard>
            );
          })}
        </group>
      )}
    </>
  );
};

// Preload the math tower model
useGLTF.preload(MATH_TOWER_MODEL_PATH);

export default Tower; 